import { cn } from '@magicdoorinc/ui';
import { A, useNavigate } from '@solidjs/router';
import { For, Show, createMemo } from 'solid-js';
import IconBackArrow from '~/assets/images/common/backArrow.svg?component-solid';
import IconHome from '~/assets/images/common/home.svg?component-solid';

export interface BreadcrumbItem {
  label: string;
  link?: string;
  linkClass?: string;
  title?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  backLink?: string | (() => void);
  loading?: boolean;
  idPrefix?: string;
}

const Breadcrumb = (props: BreadcrumbProps) => {
  const navigate = useNavigate();
  const backLink = createMemo(() => props.backLink);

  function onClickBack() {
    const link = backLink();
    if (typeof link === 'function') {
      link();
      return;
    }
    if (link === 'home') {
      return navigate('/');
    }
    link && navigate(link);
  }

  return (
    <div class="flex items-center justify-center bg-white py-1 text-sm">
      <Show when={props.backLink !== undefined && props.backLink !== 'home'}>
        <button
          id={`${props.idPrefix || 'breadcrumb'}-back-btn`}
          type="button"
          onClick={() => onClickBack()}
          class="mr-1 inline-flex size-8 shrink-0 items-center justify-center gap-3 text-nowrap rounded-md bg-white font-medium text-text-02 transition-all hover-allowed:hover:bg-primary-light hover-allowed:hover:text-primary hover-allowed:active:bg-primary-light-active">
          <IconBackArrow class="text-inherit transition-all" />
        </button>
      </Show>
      <Show when={props.backLink === 'home'}>
        <button
          id={`${props.idPrefix || 'breadcrumb'}-back-btn`}
          type="button"
          onClick={() => onClickBack()}
          class="mr-1 inline-flex size-8 shrink-0 items-center justify-center gap-3 text-nowrap rounded-md bg-white font-medium text-text-02 transition-all hover-allowed:hover:bg-primary-light hover-allowed:hover:text-primary hover-allowed:active:bg-primary-light-active">
          <IconHome class="text-inherit transition-all" />
        </button>
      </Show>
      <nav
        aria-label="breadcrumb"
        class="w-full overflow-hidden text-sm"
        classList={{
          'pointer-events-none': props.loading,
        }}>
        <ol class="flex space-x-1.5 overflow-hidden">
          <For each={props.items}>
            {(item, index) => (
              <>
                <Show when={item.link} fallback={<li class="real-li h-full truncate">{item.label}</li>}>
                  <li class={cn({ hidden: index() < props.items.length - 1 }, 'h-5 xs:block', 'real-li')}>
                    <A
                      id={`${props.idPrefix || 'breadcrumb'}-item-${index()}`}
                      href={item.link as string}
                      class={cn(
                        item.linkClass || 'text-text-02 transition-all hover-allowed:hover:text-primary-hover',
                        'inline-block h-full truncate whitespace-nowrap'
                      )}
                      title={item.label === '...' ? item.title : item.label}>
                      {item.label}
                    </A>
                  </li>
                </Show>
                <Show when={index() < props.items.length - 1}>
                  <li class="hidden text-text-disabled xs:block">&gt; </li>
                </Show>
              </>
            )}
          </For>
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumb;
